import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { SearchTextField } from "@/controls/quick-filter-textfield/SearchTextField";
import { useGridStyles } from "@/pages/laboratory/styles/gridStyles";
import { useContextMenuMESF } from "@dexteel/mesf-core";
import { Button, Grid, Typography } from "@material-ui/core";
import { GridApi } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useState } from "react";
import { useGetMenuOptions } from "../hooks/useGetMenuOptions";
import { useMoldSizeOptionsFunctions } from "../hooks/useMoldSizeOptionsFunctions";
import { useTableData } from "../hooks/useTableData";
import { CreateMoldSize, DeleteMoldSize, EditMoldSize } from "./modals";

type Props = {
	getCatalogMoldSizesFromAPI: Function;
	isLoading: boolean;
};

export const TableMoldSize = ({
	getCatalogMoldSizesFromAPI,
	isLoading,
}: Props) => {
	const classes = useGridStyles();
	const [selectedRowId, setSelectedRowId] = useState("");
	const [showCreateModal, setShowCreateModal] = useState(false);
	const [showEditModal, setShowEditModal] = useState(false);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [moldSizeId, setMoldSizeId] = useState<number | null>(null);
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [quickFilterText, setQuickFilterText] = useState("");

	const { getMenuOptions } = useMoldSizeOptionsFunctions({
		setMoldSizeId,
		setShowEditModal,
		setShowDeleteModal,
		setShowCreateModal,
	});

	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const { rows, columnDefs, defaultColDef } = useTableData({
		setMoldSizeId,
		setShowEditModal,
		setShowDeleteModal,
		showContextMenu,
	});

	const { columnDefaults } = useGridDefinitions({
		OnEdit: (data: any) => {
			setShowEditModal(!showEditModal);
			setMoldSizeId(data.MoldSizeId);
		},
	});

	const { getContextMenuItems } = useGetMenuOptions({
		rows,
		selectedRowId,
		setSelectedRowId,
		showContextMenu,
	});

	columnDefaults.floatingFilter = false;

	const onRowDoubleClicked = (event: any) => {
		setMoldSizeId(event.data.MoldSizeId);
		setShowEditModal(true);
	};

	useEffect(() => {
		if (isLoading) {
			gridApi?.showLoadingOverlay();
		} else {
			gridApi?.hideOverlay();
		}
	}, [isLoading, rows, gridApi]);

	useEffect(() => {
		registerConfig({
			id: "moldSizeContext",
			getOptions: getMenuOptions,
		});
	}, []);

	return (
		<>
			<Grid item xs={12} container justifyContent="center" spacing={1}>
				<Grid item md={12} xs={12}>
					<Typography variant="h5" style={{ fontWeight: 600 }}>
						Mold Sizes
					</Typography>
				</Grid>

				<Grid
					item
					xs={12}
					container
					justifyContent="flex-end"
					alignContent="center"
				>
					<Grid item xs={12} style={{ marginBottom: 10 }}>
						<SearchTextField
							value={quickFilterText}
							onChange={(text) => setQuickFilterText(text)}
							label="Search mold sizes"
						/>
					</Grid>
					<Grid
						item
						md={12}
						xs={12}
						style={{ height: "68vh" }}
						className={classes.borderAgGridTable}
					>
						<div style={{ height: "100%", width: "100%" }}>
							<AgGridReact
								rowData={rows || []}
								columnDefs={columnDefs as any}
								getContextMenuItems={getContextMenuItems}
								onRowDoubleClicked={onRowDoubleClicked}
								getRowId={(params) => String(params.data.MoldSizeId)}
								onGridReady={(params) => setGridApi(params.api)}
								enableCellTextSelection={true}
								suppressContextMenu={false}
								allowContextMenuWithControlKey={false}
								rowSelection="single"
								quickFilterText={quickFilterText}
							/>
						</div>
					</Grid>

					<Grid item md={"auto"} xs={12} style={{ marginTop: 10 }}>
						<Button
							variant="contained"
							color="primary"
							fullWidth
							style={{ minWidth: 250 }}
							onClick={() => setShowCreateModal(!showCreateModal)}
						>
							NEW MOLD SIZE
						</Button>
					</Grid>
				</Grid>
			</Grid>

			<CreateMoldSize
				show={showCreateModal}
				onHide={(moldSizeCreated) => {
					setShowCreateModal(false);
					setTimeout(() => {
						if (gridApi && moldSizeCreated) {
							gridApi.paginationGoToLastPage();
							getCatalogMoldSizesFromAPI();
						}
					}, 100);
				}}
			/>
			<EditMoldSize
				show={showEditModal}
				onHide={(updated: boolean) => {
					setShowEditModal(false);
					setMoldSizeId(null);
					if (updated) getCatalogMoldSizesFromAPI();
				}}
				moldSizeId={moldSizeId}
			/>
			<DeleteMoldSize
				show={showDeleteModal}
				onHide={(updated: boolean) => {
					setShowDeleteModal(false);
					setMoldSizeId(null);
					if (updated) getCatalogMoldSizesFromAPI();
				}}
				moldSizeId={moldSizeId}
			/>
		</>
	);
};
